// src/modules/app-views/account/pages/Init/modals/ChangePhoneModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo, useRef } from 'react';
import { MobileOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';
import authService from 'services/authService';
import { mergeUrlParamsQuery } from 'utils/urlUtils';
import { API } from 'constants/Api';
import verifyCurrentPhoneStep from './Step1';
import enterNewPhoneStep from './Step2';
import verifyNewPhoneStep from './Step3';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ChangePhoneModal = ({ show, setShow, afterClose }) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const storedUser = useZustand('auth', 'user');

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const newPhoneDataRef = useRef({ phone: '', countryCode: 'BR' });

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const currentPhone = storedUser?.phone?.value || '';
  const currentPhoneCountryCode = storedUser?.phone?.countryCode || 'BR';

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleFinish = async () => {
    const { put } = useZustand('global', 'useApi', false);

    // Get phone data from ref (set in Step2)
    const newPhone = newPhoneDataRef.current.phone;
    const newPhoneCountryCode = newPhoneDataRef.current.countryCode || 'BR';

    try {
      // Update phone in backend
      const result = await put({
        url: mergeUrlParamsQuery({
          url: API.ACCOUNTS.URL,
          params: { _id: storedUser._id },
        }),
        dataSet: {
          phone: {
            value: newPhone,
            countryCode: newPhoneCountryCode,
            isVerified: true,
          },
        },
        showSuccessNotification: false,
      });

      if (result) {
        // Refresh user data
        await authService.signInRefresh();

        // Show success notification
        appService.notification({
          flag: 'success',
          title: 'phone_changed_success_title',
          description: 'phone_changed_success_description',
          duration: 5,
        });

        return true; // Close modal
      }

      return false;
    } catch (err) {
      console.error('[ChangePhoneModal] Error updating phone:', err);
      await appService.message({ flag: 'e', text: 'phone_change_failed' });
      return false;
    }
  };

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------
  const steps = useMemo(
    () => [
      // Step 1: Verify current phone
      verifyCurrentPhoneStep({
        storedUser,
        currentPhone,
        currentPhoneCountryCode,
      }),

      // Step 2: Enter new phone number
      enterNewPhoneStep({
        currentPhoneCountryCode,
        newPhoneDataRef,
      }),

      // Step 3: Verify new phone
      verifyNewPhoneStep({
        storedUser,
        newPhoneDataRef,
      }),
    ],
    [storedUser, currentPhone, currentPhoneCountryCode]
  );

  // ---------------------------------------------------------------------------
  // 8. EFFECTS II
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 9. UI
  // ---------------------------------------------------------------------------
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      icon={<MobileOutlined />}
      title="change_phone"
      steps={steps}
      onFinish={handleFinish}
      afterClose={afterClose}
      width={460}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ChangePhoneModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
